/** Onboarding Detail (Demo Mode only) - the pre/during-onboarding customer detail page for the
 * Customer Management workspace, reached from the Onboarding Pipeline
 * (`../OnboardingPipeline`). Modelled on the OFD retailer onboarding wizard
 * (`pages/orderly-for-distributors/Onboarding`) - a tabbed journey through the setup categories
 * required to launch a customer - but extended with the full go-live readiness checklist
 * (commercial setup, site setup, supplier & procurement setup, product & menu readiness,
 * operational readiness, data & governance checks, launch sign-off) and focused on progress and
 * missing information rather than form capture. Once every category is complete, the launch
 * sign-off tab lets an owner approve go-live, which transitions the customer to `Live` and hands
 * off to the operational customer workspace (`../CustomerWorkspace`). All figures are static
 * mock data, matching the "seeded illustrative numbers" approach used by
 * `src/pages/EstateManagement`; checklist toggles mutate the in-memory seed data for the rest of
 * the session (see `toggleMilestoneItem` in `mocks/data/customerManagementData.ts`). */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Button, Checkbox, Chip, Grid, Stack, Step, StepLabel, Stepper, SvgIcon, Tab, Tabs,
  TextField, Typography, useTheme,
} from '@mui/material';
import {
  CheckCircle, TrendingUpOutlined, FactCheckOutlined, ReportProblemOutlined, EventAvailable,
  RadioButtonUnchecked,
} from '@mui/icons-material';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import BackButton from '../../../components/shared/actions/BackButton';
import { Section } from '../../../components/ui/Section';
import {
  CustomerRecord, CustomerServiceRequirements, getCustomer, getCustomerDocuments,
  getMilestoneProgress, getServiceRequirements, JOURNEY_STAGE_LABEL, JOURNEY_STAGE_ORDER,
  markCustomerLive, OnboardingMilestoneCategoryKey, RISK_COLOR, setMilestoneItemComplete,
  toggleMilestoneItem,
} from '../../../mocks/data/customerManagementData';
import SitesTab from '../CustomerWorkspace/components/SitesTab';
import ContactsTab from '../CustomerWorkspace/components/ContactsTab';
import MobilisationTab from '../CustomerWorkspace/components/MobilisationTab';
import EditableStringList from '../components/EditableStringList';
import DocumentChecklist from '../components/DocumentChecklist';
import HubSpotSummaryCard from '../components/HubSpotSummaryCard';

const MetricTile = ({ label, value, subtitle, icon, color }: { label: string; value: string; subtitle: string; icon: JSX.Element; color: string }) => {
  const theme = useTheme();
  return (
    <Section style={{ p: 4, mb: 0 }}>
      <Box display="flex"
        alignItems="center"
        justifyContent="space-between">
        <Box flex={1}>
          <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
            {label}
          </Box>
          <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
            {value}
          </Box>
          <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
            {subtitle}
          </Box>
        </Box>
        <Box sx={{ backgroundColor: `${color}20`, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
          <SvgIcon fontSize="medium">{icon}</SvgIcon>
        </Box>
      </Box>
    </Section>
  );
};

const OnboardingDetailPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId: string }>();
  const [, setRefreshTick] = useState(0);
  const [activeCategory, setActiveCategory] = useState<OnboardingMilestoneCategoryKey>('serviceRequirements');

  const customer = getCustomer(customerId ?? '');

  if (!customer) {
    return (
      <PageArea>
        <BackButton
          handleClick={() => navigate('/customer-management/onboarding-pipeline')}
          title={t('backToOnboardingPipeline', 'Back to Onboarding Pipeline')}
          isLink={false}
          styles={{ marginBottom: '20px' }}
        />
        <Typography variant="textMD">{t('customerNotFound', 'Client not found.')}</Typography>
      </PageArea>
    );
  }

  const progress = getMilestoneProgress(customer);
  const activeStepIndex = JOURNEY_STAGE_ORDER.indexOf(customer.journeyStage);
  const daysToGoLive = customer.targetGoLive ? Math.ceil((new Date(customer.targetGoLive).getTime() - Date.now()) / (1000 * 60 * 60 * 24)) : null;
  const canApprove = progress.overallPercent === 100;

  const handleToggle = (categoryKey: OnboardingMilestoneCategoryKey, itemKey: string) => {
    toggleMilestoneItem(customer.id, categoryKey, itemKey);
    setRefreshTick((tick) => tick + 1);
  };

  /** Mutates the live customer record in place (consistent with the checklist's lazy in-memory
   * pattern) and forces a re-render - used by the field-based sections below. */
  const mutate = (mutator: (target: CustomerRecord) => void) => {
    mutator(customer);
    setRefreshTick((tick) => tick + 1);
  };

  const setDerivedComplete = (categoryKey: OnboardingMilestoneCategoryKey, itemKey: string, complete: boolean) => {
    setMilestoneItemComplete(customer.id, categoryKey, itemKey, complete);
  };

  /** Patches the customer's service requirements (creating the record from the shared empty
   * default the first time a field is touched, for onboarding customers that weren't created
   * via the intake wizard) and derives that field's milestone completion from the new value. */
  const updateServiceRequirements = (itemIndex: number, patch: Partial<CustomerServiceRequirements>) => {
    mutate((c) => { c.serviceRequirements = { ...getServiceRequirements(c), ...patch }; });
    const value = Object.values(patch)[0];
    const complete = Array.isArray(value) ? value.filter(Boolean).length > 0 : !!value;
    setDerivedComplete('serviceRequirements', `serviceRequirements-${itemIndex}`, complete);
  };

  const handleApproveGoLive = () => {
    markCustomerLive(customer.id);
    navigate(`/customer-management/customers/${customer.id}`);
  };

  const renderStatusRow = (label: string, complete: boolean) => (
    <Stack direction="row"
      alignItems="center"
      spacing={1}
      key={label}>
      {complete ? <CheckCircle fontSize="small"
        color="success" /> : <RadioButtonUnchecked fontSize="small"
        color="disabled" />}
      <Typography variant="textSM"
        sx={{ color: complete ? 'text.secondary' : 'text.primary' }}>{label}</Typography>
    </Stack>
  );

  const METRIC_TILES = [
    { label: t('onboardingDetailProgress', 'Go-live readiness'), value: `${progress.overallPercent}%`, subtitle: t('onboardingDetailProgressSub', 'Across all milestone categories'), icon: <TrendingUpOutlined />, color: '#2e7d32' },
    { label: t('onboardingDetailCategories', 'Categories complete'), value: `${progress.completedCategories} / ${progress.totalCategories}`, subtitle: t('onboardingDetailCategoriesSub', 'Fully checked off'), icon: <FactCheckOutlined />, color: '#1976d2' },
    { label: t('onboardingDetailMissing', 'Missing items'), value: `${progress.totalIncompleteItems}`, subtitle: t('onboardingDetailMissingSub', 'Still outstanding'), icon: <ReportProblemOutlined />, color: '#d32f2f' },
    { label: t('onboardingDetailDaysToGoLive', 'Days to target go-live'), value: daysToGoLive === null ? t('onboardingPipelineNotSet', 'Not set') : `${daysToGoLive}`, subtitle: customer.targetGoLive || t('onboardingDetailNoDate', 'No date agreed'), icon: <EventAvailable />, color: '#ed6c02' },
  ];

  return (
    <PageArea>
      <BackButton
        handleClick={() => navigate('/customer-management/onboarding-pipeline')}
        title={t('backToOnboardingPipeline', 'Back to Onboarding Pipeline')}
        isLink={false}
        styles={{ marginBottom: '20px' }}
      />

      <PageTitle subTitle={t('onboardingDetailSubTitle', 'Work through every go-live readiness milestone for this client, then approve go-live once nothing is missing.')}>
        {customer.name}
      </PageTitle>

      <Section style={{ p: 4, mb: 4, mt: 4 }}>
        <Stepper activeStep={activeStepIndex}
          alternativeLabel>
          {JOURNEY_STAGE_ORDER.map((stage) => (
            <Step key={stage}>
              <StepLabel>{t(`journeyStage${stage}`, JOURNEY_STAGE_LABEL[stage])}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Section>

      <Grid container
        spacing={4}
        sx={{ mb: 6 }}>
        {METRIC_TILES.map((tile) => (
          <Grid item
            xs={12}
            sm={6}
            md={3}
            key={tile.label}>
            <MetricTile {...tile} />
          </Grid>
        ))}
      </Grid>

      <Grid container
        spacing={4}
        sx={{ mb: 6 }}>
        <Grid item
          xs={12}
          md={4}>
          <HubSpotSummaryCard customer={customer}
            onUpdated={() => setRefreshTick((tick) => tick + 1)} />
        </Grid>
      </Grid>

      <Tabs
        value={activeCategory}
        onChange={(_, value) => setActiveCategory(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}
      >
        {progress.categories.map((category) => (
          <Tab
            key={category.key}
            value={category.key}
            label={category.label}
            icon={category.percent === 100 ? <CheckCircle fontSize="small"
              color="success" /> : undefined}
            iconPosition="end"
          />
        ))}
      </Tabs>

      <Grid container
        spacing={4}>
        {activeCategory === 'serviceRequirements' && (
          <Grid item
            xs={12}>
            <Section style={{ p: 5 }}>
              <Grid container
                spacing={5}>
                <Grid item
                  xs={12}
                  md={6}>
                  <EditableStringList
                    label={t('onboardingDetailUserTypes', 'Type of users')}
                    items={getServiceRequirements(customer).userTypes}
                    onChange={(userTypes) => updateServiceRequirements(0, { userTypes })}
                    addLabel={t('onboardingDetailAddUserType', 'Add user type')}
                    placeholder={t('onboardingDetailUserTypePlaceholder', 'e.g. Students, staff, visitors')}
                  />
                  <Box sx={{ mt: 3 }}>
                    <EditableStringList
                      label={t('onboardingDetailServiceTimes', 'Service times')}
                      items={getServiceRequirements(customer).serviceTimes}
                      onChange={(serviceTimes) => updateServiceRequirements(1, { serviceTimes })}
                      addLabel={t('onboardingDetailAddServiceTime', 'Add service time')}
                      placeholder={t('onboardingDetailServiceTimePlaceholder', 'e.g. Breakfast 7-9am')}
                    />
                  </Box>
                </Grid>

                <Grid item
                  xs={12}
                  md={6}>
                  <EditableStringList
                    label={t('onboardingDetailMenus', 'Menus')}
                    items={getServiceRequirements(customer).menus}
                    onChange={(menus) => updateServiceRequirements(2, { menus })}
                    addLabel={t('onboardingDetailAddMenu', 'Add menu')}
                    placeholder={t('onboardingDetailMenuPlaceholder', 'e.g. All-day dining menu')}
                  />
                  <Box sx={{ mt: 3 }}>
                    <EditableStringList
                      label={t('onboardingDetailDietaryRequirements', 'Dietary requirements')}
                      items={getServiceRequirements(customer).dietaryRequirements}
                      onChange={(dietaryRequirements) => updateServiceRequirements(3, { dietaryRequirements })}
                      addLabel={t('onboardingDetailAddDietaryRequirement', 'Add dietary requirement')}
                      placeholder={t('onboardingDetailDietaryRequirementPlaceholder', 'e.g. Vegan, halal, gluten-free')}
                    />
                  </Box>
                  <Box sx={{ mt: 3 }}>
                    <EditableStringList
                      label={t('onboardingDetailAllergens', 'Allergens')}
                      items={getServiceRequirements(customer).allergens}
                      onChange={(allergens) => updateServiceRequirements(4, { allergens })}
                      addLabel={t('onboardingDetailAddAllergen', 'Add allergen')}
                      placeholder={t('onboardingDetailAllergenPlaceholder', 'e.g. Nuts, dairy, shellfish')}
                    />
                  </Box>
                </Grid>

                <Grid item
                  xs={12}
                  md={6}>
                  <Typography variant="textSM"
                    component="div"
                    fontWeight={600}
                    sx={{ mb: 2 }}>{t('onboardingDetailHospitalityBudget', 'Hospitality & budget')}</Typography>
                  <Grid container
                    spacing={2}>
                    <Grid item
                      xs={12}>
                      <TextField
                        label={t('onboardingDetailHospitalityNeeds', 'Hospitality needs')}
                        value={getServiceRequirements(customer).hospitalityNeeds}
                        onChange={(e) => updateServiceRequirements(5, { hospitalityNeeds: e.target.value })}
                        fullWidth
                        multiline
                        minRows={2}
                        size="small"
                      />
                    </Grid>
                    <Grid item
                      xs={6}>
                      <TextField
                        label={t('onboardingDetailBudget', 'Budget')}
                        value={getServiceRequirements(customer).budget}
                        onChange={(e) => updateServiceRequirements(6, { budget: e.target.value })}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item
                      xs={6}>
                      <TextField
                        label={t('onboardingDetailPricingModel', 'Pricing model')}
                        value={getServiceRequirements(customer).pricingModel}
                        onChange={(e) => updateServiceRequirements(7, { pricingModel: e.target.value })}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item
                  xs={12}
                  md={6}>
                  <EditableStringList
                    label={t('onboardingDetailComplianceExpectations', 'Compliance expectations')}
                    items={getServiceRequirements(customer).complianceExpectations}
                    onChange={(complianceExpectations) => updateServiceRequirements(8, { complianceExpectations })}
                    addLabel={t('onboardingDetailAddComplianceExpectation', 'Add compliance expectation')}
                    placeholder={t('onboardingDetailComplianceExpectationPlaceholder', 'e.g. HACCP certification required')}
                  />
                  <Box sx={{ mt: 3 }}>
                    <EditableStringList
                      label={t('onboardingDetailReportingRequirements', 'Reporting requirements')}
                      items={getServiceRequirements(customer).reportingRequirements}
                      onChange={(reportingRequirements) => updateServiceRequirements(9, { reportingRequirements })}
                      addLabel={t('onboardingDetailAddReportingRequirement', 'Add reporting requirement')}
                      placeholder={t('onboardingDetailReportingRequirementPlaceholder', 'e.g. Monthly spend report')}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Section>
          </Grid>
        )}

        {activeCategory === 'commercialSetup' && (
          <Grid item
            xs={12}>
            <Section style={{ p: 5 }}>
              <Grid container
                spacing={5}>
                <Grid item
                  xs={12}
                  md={6}>
                  <Typography variant="textSM"
                    component="div"
                    fontWeight={600}
                    sx={{ mb: 2 }}>{t('onboardingDetailContractSummary', 'Contract summary')}</Typography>
                  <Grid container
                    spacing={2}>
                    <Grid item
                      xs={12}>
                      <TextField
                        label={t('contractsRef', 'Contract Ref.')}
                        value={customer.contract.contractRef}
                        onChange={(e) => { mutate((c) => { c.contract.contractRef = e.target.value; }); setDerivedComplete('commercialSetup', 'commercialSetup-1', !!e.target.value); }}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item
                      xs={6}>
                      <TextField
                        label={t('contractsStartDate', 'Start Date')}
                        type="date"
                        value={customer.contract.startDate}
                        onChange={(e) => { mutate((c) => { c.contract.startDate = e.target.value; }); setDerivedComplete('commercialSetup', 'commercialSetup-2', !!(e.target.value && customer.contract.endDate && customer.contract.noticePeriod)); }}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item
                      xs={6}>
                      <TextField
                        label={t('contractsEndDate', 'End Date')}
                        type="date"
                        value={customer.contract.endDate}
                        onChange={(e) => { mutate((c) => { c.contract.endDate = e.target.value; }); setDerivedComplete('commercialSetup', 'commercialSetup-2', !!(customer.contract.startDate && e.target.value && customer.contract.noticePeriod)); }}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item
                      xs={12}>
                      <TextField
                        label={t('contractsNoticePeriod', 'Notice Period')}
                        value={customer.contract.noticePeriod}
                        onChange={(e) => { mutate((c) => { c.contract.noticePeriod = e.target.value; }); setDerivedComplete('commercialSetup', 'commercialSetup-2', !!(customer.contract.startDate && customer.contract.endDate && e.target.value)); }}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item
                      xs={12}>
                      <TextField
                        label={t('onboardingDetailOperatingModel', 'Location group / operating model')}
                        value={customer.operatingModel}
                        onChange={(e) => { mutate((c) => { c.operatingModel = e.target.value; }); setDerivedComplete('commercialSetup', 'commercialSetup-3', !!e.target.value); }}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item
                  xs={12}
                  md={6}>
                  <EditableStringList
                    label={t('contractsObligations', 'Reporting obligations')}
                    items={customer.contract.obligations}
                    onChange={(obligations) => { mutate((c) => { c.contract.obligations = obligations; }); setDerivedComplete('commercialSetup', 'commercialSetup-4', obligations.filter(Boolean).length > 0); }}
                    addLabel={t('contractsAddObligation', 'Add obligation')}
                    placeholder={t('contractsObligationPlaceholder', 'e.g. Quarterly nutrition reporting')}
                  />
                </Grid>

                <Grid item
                  xs={12}>
                  <Typography variant="textSM"
                    component="div"
                    fontWeight={600}
                    sx={{ mb: 2 }}>{t('onboardingDetailMilestoneStatus', 'Milestone status')}</Typography>
                  <Stack spacing={1.5}>
                    {renderStatusRow(t('onboardingMilestoneCustomerRecord', 'Client record created'), true)}
                    {renderStatusRow(t('onboardingMilestoneContractCaptured', 'Contract captured'), !!customer.contract.contractRef)}
                    {renderStatusRow(t('onboardingMilestoneContractDates', 'Contract dates, renewal dates and notice periods recorded'), !!(customer.contract.startDate && customer.contract.endDate && customer.contract.noticePeriod))}
                    {renderStatusRow(t('onboardingMilestoneSiteGroup', 'Location group or operating model defined'), !!customer.operatingModel)}
                    {renderStatusRow(t('onboardingMilestoneReportingObligations', 'Reporting obligations captured'), customer.contract.obligations.filter(Boolean).length > 0)}
                  </Stack>
                </Grid>

                <Grid item
                  xs={12}>
                  <Typography variant="textSM"
                    component="div"
                    fontWeight={600}
                    sx={{ mb: 2 }}>{t('contractsDocumentsTitle', 'Contracts & legal agreements')}</Typography>
                  <DocumentChecklist
                    documents={getCustomerDocuments(customer)}
                    headerFilter="Contracts & Legal Agreements"
                    mode="manage"
                    onChange={(documents) => mutate((c) => { c.documents = documents; })}
                  />
                </Grid>
              </Grid>
            </Section>
          </Grid>
        )}

        {activeCategory === 'siteSetup' && (
          <Grid item
            xs={12}>
            <SitesTab customer={customer} />
            <Section style={{ p: 4, mt: 4 }}>
              <Typography variant="textSM"
                fontWeight={600}
                sx={{ mb: 2 }}>{t('onboardingDetailMilestoneStatus', 'Milestone status')}</Typography>
              <Stack spacing={1.5}>
                {renderStatusRow(t('onboardingMilestoneSitesCreated', 'Locations created'), customer.sites.length > 0)}
                {renderStatusRow(t('onboardingMilestoneSiteContacts', 'Location contacts and ownership assigned'), customer.contacts.length > 0)}
                <Box display="flex"
                  alignItems="center">
                  <Checkbox
                    checked={customer.onboardingMilestones.find((c) => c.key === 'siteSetup')?.items[2]?.complete ?? false}
                    onChange={() => handleToggle('siteSetup', 'siteSetup-2')}
                    size="small"
                  />
                  <Typography variant="textSM">{t('onboardingMilestoneTradingCalendars', 'Trading calendars, opening days and delivery windows configured')}</Typography>
                </Box>
                {renderStatusRow(t('onboardingMilestoneSiteRequirements', 'Location-specific requirements captured'), customer.sites.length > 0 && customer.sites.every((site) => !!site.requirements))}
              </Stack>
            </Section>
          </Grid>
        )}

        {activeCategory === 'supplierProcurementSetup' && (
          <Grid item
            xs={12}>
            <Section style={{ p: 4 }}>
              <Typography variant="textSM"
                fontWeight={600}
                sx={{ mb: 2 }}>{t('onboardingDetailSupplierProcurement', 'Supplier & procurement setup')}</Typography>
              <Typography variant="textSM"
                color="text.secondary">
                {t('onboardingDetailSupplierProcurementNote', 'Approved suppliers, price lists and catalogue governance are configured in the Procurement workspace once locations are live.')}
              </Typography>
            </Section>
          </Grid>
        )}

        {activeCategory === 'productMenuReadiness' && (
          <Grid item
            xs={12}>
            <Section style={{ p: 4 }}>
              <Typography variant="textSM"
                fontWeight={600}
                sx={{ mb: 2 }}>{t('onboardingDetailProductMenu', 'Product & menu readiness')}</Typography>
              <Typography variant="textSM"
                color="text.secondary">
                {t('onboardingDetailProductMenuNote', 'Ingredients, allergens, pack sizes and pricing are captured in Range Planning and Recipes once the catalogue is loaded.')}
              </Typography>
            </Section>
          </Grid>
        )}

        {activeCategory === 'operationalReadiness' && (
          <Grid item
            xs={12}>
            <Grid container
              spacing={4}>
              <Grid item
                xs={12}
                md={6}>
                <ContactsTab customer={customer} />
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <MobilisationTab customer={customer} />
              </Grid>
            </Grid>
          </Grid>
        )}

        {activeCategory === 'dataGovernanceChecks' && (
          <Grid item
            xs={12}>
            <Section style={{ p: 5 }}>
              <Grid container
                spacing={5}>
                <Grid item
                  xs={12}
                  md={6}>
                  <Typography variant="textSM"
                    component="div"
                    fontWeight={600}
                    sx={{ mb: 2 }}>{t('onboardingDetailDataGovernance', 'Data & governance checks')}</Typography>
                  <Stack direction="row"
                    spacing={1}
                    sx={{ mb: 1 }}>
                    <Chip label={`${t('onboardingDetailRiskLevel', 'Risk level')}: ${customer.riskLevel}`}
                      size="small"
                      color={RISK_COLOR[customer.riskLevel]} />
                  </Stack>
                  {customer.blockers.length === 0 && customer.missingInfo.length === 0 ? (
                    <Typography variant="textSM"
                      color="text.secondary">{t('customerOverviewNoRisks', 'No open risks or missing information.')}</Typography>
                  ) : (
                    <Stack spacing={0.5}>
                      {customer.blockers.map((blocker) => (
                        <Typography key={blocker}
                          variant="textSM">• {blocker}</Typography>
                      ))}
                      {customer.missingInfo.map((item) => (
                        <Typography key={item}
                          variant="textSM">• {t('customerOverviewMissingPrefix', 'Missing')}: {item}</Typography>
                      ))}
                    </Stack>
                  )}
                </Grid>
                <Grid item
                  xs={12}>
                  <Typography variant="textSM"
                    component="div"
                    fontWeight={600}
                    sx={{ mb: 2 }}>{t('onboardingDetailDueDiligence', 'Due diligence documents')}</Typography>
                  <DocumentChecklist
                    documents={getCustomerDocuments(customer)}
                    headerFilter="Due Diligence"
                    mode="manage"
                    onChange={(documents) => mutate((c) => { c.documents = documents; })}
                  />
                </Grid>
              </Grid>
            </Section>
          </Grid>
        )}

        {activeCategory === 'launchSignOff' && (
          <Grid item
            xs={12}>
            <Section style={{ p: 4 }}>
              <Typography variant="textSM"
                fontWeight={600}
                sx={{ mb: 2 }}>{t('onboardingDetailLaunchSignOff', 'Launch sign-off')}</Typography>
              <Grid container
                spacing={2}
                sx={{ mb: 3 }}>
                <Grid item
                  xs={12}
                  sm={6}>
                  <TextField
                    label={t('onboardingMilestoneInternalSignOff', 'Internal owner sign-off date')}
                    type="date"
                    value={customer.internalSignOffDate ?? ''}
                    onChange={(e) => { mutate((c) => { c.internalSignOffDate = e.target.value; }); setDerivedComplete('launchSignOff', 'launchSignOff-0', !!e.target.value); }}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item
                  xs={12}
                  sm={6}>
                  <TextField
                    label={t('onboardingMilestoneCustomerSignOff', 'Client owner sign-off date')}
                    type="date"
                    value={customer.customerSignOffDate ?? ''}
                    onChange={(e) => { mutate((c) => { c.customerSignOffDate = e.target.value; }); setDerivedComplete('launchSignOff', 'launchSignOff-1', !!e.target.value); }}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item
                  xs={12}
                  sm={6}>
                  <TextField
                    label={t('onboardingMilestoneGoLiveDate', 'Go-live date confirmed')}
                    type="date"
                    value={customer.targetGoLive}
                    onChange={(e) => { mutate((c) => { c.targetGoLive = e.target.value; }); setDerivedComplete('launchSignOff', 'launchSignOff-2', !!e.target.value); }}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item
                  xs={12}
                  sm={6}>
                  <TextField
                    label={t('onboardingMilestoneHypercareStart', 'Hypercare period start date')}
                    type="date"
                    value={customer.hypercareStartDate ?? ''}
                    onChange={(e) => { mutate((c) => { c.hypercareStartDate = e.target.value; }); setDerivedComplete('launchSignOff', 'launchSignOff-3', !!e.target.value); }}
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
              <Button
                variant="primary"
                disabled={!canApprove}
                onClick={handleApproveGoLive}
              >
                {t('onboardingDetailApproveGoLive', 'Approve Go-Live')}
              </Button>
              {!canApprove && (
                <Typography variant="textXS"
                  color="text.secondary"
                  sx={{ mt: 1 }}>
                  {t('onboardingDetailApproveGoLiveHint', 'Complete every milestone category before approving go-live.')}
                </Typography>
              )}
            </Section>
          </Grid>
        )}
      </Grid>
    </PageArea>
  );
};

export default OnboardingDetailPage;
